import { IconButton, Link, Tooltip, Typography } from "@mui/material"
import { ArticleRounded, CheckCircleRounded, CloudDownloadRounded, DeleteRounded, LockRounded, RadioButtonUncheckedRounded } from "@mui/icons-material"
import { green } from "@mui/material/colors"
import { jobOrderColNm } from "constants/jobOrderConstant"
import dayjs from "dayjs"
import { numberFormat } from "utils"

// coverage status -> label / trip ExpId (the flat board shows one wage line per row)
export const STS_LABEL = { 1: "รับ", 3: "ส่ง", 5: "คืน" }

// descriptive column shown only once per JobOrdId+driver group (blank on repeat rows)
export const groupCol = (field, headerName, width, align) => ({
  field, headerName, width, align, sortable: false,
  renderCell: ({ row }) => row.isGroupStart
    ? <Typography variant="body2" noWrap>{row[field] ?? ""}</Typography>
    : <></>,
})

export const flatColumns = ({ onRecalcRow, onMovePayTm, onDeleteRow }) => [
  {
    field: "ShowDetail", headerName: "", width: 40, align: "center", sortable: false,
    renderCell: ({ row }) => (row.isGroupStart && row.JobOrdId != null)
      ? <IconButton size="small"><ArticleRounded color="primary" /></IconButton>
      : <></>,
  },
  groupCol("JobOrdId", jobOrderColNm.JobOrdId, 75, "center"),
  groupCol("JobSNm", jobOrderColNm.JobSNm, 110),
  groupCol("ShpmTypAbbr", "ประเภท", 55),
  groupCol("ContNo", jobOrderColNm.ContNo, 110),
  groupCol("ContSizeFull", jobOrderColNm.ContSizeFull, 60),
  groupCol("TakePlc", jobOrderColNm.TakePlc, 90),
  groupCol("Loc", jobOrderColNm.Loc, 90),
  groupCol("RtnPlc", jobOrderColNm.RtnPlc, 90),
  groupCol("DrvNm", "พขร.", 130),
  {
    // one appointment per job order, so show it once per group like the descriptive columns
    field: "AptTm", headerName: jobOrderColNm.AptTm, width: 100, sortable: false,
    renderCell: ({ row }) => row.isGroupStart
      ? <Typography variant="body2" noWrap>{row.AptTm ? dayjs(row.AptTm).format("DD/MM HH:mm") : ""}</Typography>
      : <></>,
  },
  {
    field: "StsTm", headerName: "วันที่วิ่งงาน", width: 100, sortable: false,
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM HH:mm") : "-",
  },
  {
    field: "IsCmpt", headerName: "สถานะหน้าที่", width: 90, align: "center", headerAlign: "center", sortable: false,
    renderCell: ({ row }) => row.IsCmpt == null
      ? <></>
      : (row.IsCmpt === 1
        ? <Tooltip title="เสร็จสิ้น" placement="top"><CheckCircleRounded fontSize="small" sx={{ color: green[600] }} /></Tooltip>
        : <Tooltip title="ยังไม่เสร็จ" placement="top"><RadioButtonUncheckedRounded fontSize="small" color="disabled" /></Tooltip>),
  },
  {
    field: "ExpNm", headerName: "รายการ", width: 150, sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2">
        {row.ExpNm || (STS_LABEL[row.ContStsId] ? `เบี้ยเลี้ยง-${STS_LABEL[row.ContStsId]}` : "")}
      </Typography>
    ),
  },
  {
    field: "Amnt", headerName: "จำนวน", width: 80, type: "number", align: "right", headerAlign: "right",
    editable: true, sortable: false,
    valueFormatter: ({ value }) => (value == null || value === "") ? "" : numberFormat(value),
  },
  {
    field: "cal", headerName: "", width: 44, align: "center", sortable: false,
    // only blank legs: calc never overwrites a leg that already has a line, so the icon
    // would do nothing there (delete the line first — the bin is in the Lock column)
    renderCell: ({ row }) => (row.ContStsId && !row.IsLocked && !row.WgItmId) ? (
      <Tooltip title="คำนวณค่าเที่ยว (แถวนี้)" placement="top">
        <IconButton size="small" sx={{ p: 0 }} onClick={() => onRecalcRow(row)}>
          <CloudDownloadRounded color="primary" />
        </IconButton>
      </Tooltip>
    ) : null,
  },
  {
    field: "PayTm", headerName: "งวดที่จ่าย", width: 110, sortable: false,
    renderCell: ({ row }) => {
      if (!row.WgItmId) return <Typography variant="caption" color="text.secondary">—</Typography>
      // The งวด set here (stored on the line's own accrual document) IS what puts the line in
      // that month's payroll. ✎ = editable, ○ = งวด set but that month's payroll has not been
      // created yet (the line joins it the moment it is), plain text = the payroll is confirmed/paid
      const label = row.SalDte ? dayjs(row.SalDte).format("MM/YYYY") : "ค้างจ่าย"
      if (row.IsLocked) return <Typography variant="body2">{label}</Typography>
      const pending = row.SalDte && !row.PayrollId
      const cell = <Link component="button" underline="hover" sx={{ fontSize: 13 }}
        onClick={() => onMovePayTm(row)}>{label} {pending ? "○" : "✎"}</Link>
      return pending
        ? <Tooltip title="ระบุงวดแล้ว ยังไม่ได้สร้างงวดเงินเดือนเดือนนี้" placement="top"><span>{cell}</span></Tooltip>
        : cell
    },
  },
  {
    field: "Lock", headerName: "", width: 46, align: "center", sortable: false,
    renderCell: ({ row }) => row.IsLocked
      ? <LockRounded fontSize="small" color="disabled" />
      : (row.WgItmId
        ? <IconButton size="small" sx={{ p: 0 }} onClick={() => onDeleteRow(row)}>
            <DeleteRounded fontSize="small" color="action" />
          </IconButton>
        : null),
  },
]
